/**
 * Canonical, role-aware onboarding state machine — the SINGLE source of truth
 * for step ordering, advancement, progress and routing across all three
 * personas. Every screen, layout, and router that needs to know "what step am
 * I on / what's next / how far along / where does it live" derives from this
 * file rather than hand-rolling its own list.
 *
 * This replaces three previously-divergent lists as the source of ordering:
 *   - `STARTUP_ONBOARDING_STEPS`   (src/components/OnboardingLayout.tsx)
 *   - `INVESTOR_ONBOARDING_STEPS`  (src/components/OnboardingLayout.tsx)
 *   - the flow ids in `src/lib/onboarding/steps.ts`
 * The `steps.ts` list now DERIVES its core ordering from this machine (see
 * `STARTUP_ONBOARDING_STEP_IDS` there); the founder-flow reshape (Task 3)
 * removed the legacy `welcome` bookend, so the live list is the machine's
 * founder flow plus the shared `complete` result screen. Do NOT reintroduce a
 * competing hard-coded list — extend the machine.
 *
 * NOTE ON STEP IDS: the machine models the TARGET flows agreed in the redesign
 * proposal (§5). Founder drops `welcome`/`complete` from the counted steps and
 * renames the data-source step to `activation`; investor collapses to a 2-step
 * identity+thesis flow. Screens that still render the legacy shape reach the
 * machine through the compatibility mappings in `steps.ts`/`resolve-route.ts`
 * until their persona task migrates them.
 */
import type { Href } from 'expo-router';

export type Persona = 'founder' | 'invited_member' | 'investor';

export type FounderStep = 'personal' | 'company' | 'essentials' | 'activation';
export type InvitedMemberStep = 'invite' | 'personal';
export type InvestorStep = 'identity' | 'thesis';

/** Any step id across all personas. `personal` is shared by founder + invited_member. */
export type StepId = FounderStep | InvitedMemberStep | InvestorStep;

/** Ordered step lists per persona — the ordering source of truth. */
const FLOWS: {
  founder: FounderStep[];
  invited_member: InvitedMemberStep[];
  investor: InvestorStep[];
} = {
  founder: ['personal', 'company', 'essentials', 'activation'],
  invited_member: ['invite', 'personal'],
  investor: ['identity', 'thesis'],
};

/**
 * Route each step renders at. Exported so the legacy compatibility layers
 * (`steps.ts`, `resolve-route.ts`, `OnboardingLayout.tsx`) can source their
 * founder/invited routes from here instead of duplicating string literals.
 *
 * The investor entries point at the real 2-step flow screens (`investor-identity`
 * + `investor-thesis`) built by Task 6, which split the former `investor-personal`
 * screen and route through these entries.
 */
export const STEP_ROUTES: {
  founder: Record<FounderStep, string>;
  invited_member: Record<InvitedMemberStep, string>;
  investor: Record<InvestorStep, string>;
} = {
  founder: {
    personal: '/(onboarding)/startup-personal',
    company: '/(onboarding)/startup-company',
    essentials: '/(onboarding)/startup-essentials',
    activation: '/(onboarding)/data-input-choice',
  },
  invited_member: {
    invite: '/invite/team',
    personal: '/(onboarding)/startup-personal',
  },
  investor: {
    identity: '/(onboarding)/investor-identity',
    thesis: '/(onboarding)/investor-thesis',
  },
};

/** The ordered step ids for a persona. Returns a fresh array (safe to mutate). */
export function getFlow(persona: Persona): StepId[] {
  return [...FLOWS[persona]];
}

/**
 * The step after `current` in the persona's flow, or the sentinel `'complete'`
 * when `current` is the final step. Throws if `current` is not part of the
 * persona's flow — a programmer error the type system cannot catch when a
 * shared id (e.g. `personal`) is passed with the wrong persona.
 */
export function nextStep(persona: Persona, current: StepId): StepId | 'complete' {
  const flow = FLOWS[persona] as StepId[];
  const idx = flow.indexOf(current);
  if (idx === -1) {
    throw new Error(`onboarding.machine: step "${current}" is not in the "${persona}" flow`);
  }
  return idx === flow.length - 1 ? 'complete' : flow[idx + 1];
}

/**
 * Zero-based position (`index`) and step count (`total`) for a step within its
 * persona flow. Drives all progress UI ("Step X of Y", dots) so the count is
 * always generated from the real path — never hard-coded. Throws if the step
 * is not in the persona's flow.
 */
export function progressFor(persona: Persona, step: StepId): { index: number; total: number } {
  const flow = FLOWS[persona] as StepId[];
  const idx = flow.indexOf(step);
  if (idx === -1) {
    throw new Error(`onboarding.machine: step "${step}" is not in the "${persona}" flow`);
  }
  return { index: idx, total: flow.length };
}

/** The route a given persona step renders at. Throws for an unknown pairing. */
export function routeForStep(persona: Persona, step: StepId): Href {
  const path = (STEP_ROUTES[persona] as Record<string, string>)[step];
  if (!path) {
    throw new Error(`onboarding.machine: no route for "${persona}/${step}"`);
  }
  return path as Href;
}
